import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import * as api from '~/Services/Service';
import GridPeriodos from './components/GridPeriodos';
import FormPeriodo from './components/FormPeriodo';

// W-0570 — GES Matricial de Despesas — Períodos
// RBAC: 260 (Gerencia Orcamento Matricial) + 630 (portão migração HML)

const TAMANHO_PAGINA = 20;

const MatricialPeriodos = () => {
  const [periodos, setPeriodos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [filtroDescricao, setFiltroDescricao] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const [buscarTick, setBuscarTick] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [periodoAtual, setPeriodoAtual] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const [confirmRemover, setConfirmRemover] = useState(false);
  const [periodoParaRemover, setPeriodoParaRemover] = useState(null);
  const [removendo, setRemovendo] = useState(false);

  const carregarPeriodos = useCallback(
    (descricao) => {
      let ativo = true;
      setCarregando(true);
      api
        .listarMatricialPeriodos(1, 10000, descricao)
        .then((res) => {
          if (!ativo) return;
          const dados = res && res.itens ? res : { itens: [], total: 0 };
          setPeriodos(dados.itens || []);
          setTotal(dados.total || 0);
        })
        .catch((err) => {
          if (!ativo) return;
          const msg = err && err.message ? err.message : 'Erro ao carregar períodos.';
          toast.error(msg);
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
      return () => {
        ativo = false;
      };
    },
    []
  );

  useEffect(() => {
    const cleanup = carregarPeriodos(filtroAtivo);
    return cleanup;
  }, [filtroAtivo, buscarTick, carregarPeriodos]);

  const handleNovo = () => {
    setPeriodoAtual(null);
    setShowForm(true);
  };

  const handleEditar = (item) => {
    setCarregando(true);
    api
      .detalharMatricialPeriodo(item.codigo)
      .then((res) => {
        setPeriodoAtual(res);
        setShowForm(true);
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao carregar período.';
        toast.error(msg);
      })
      .finally(() => setCarregando(false));
  };

  const handleFecharForm = () => {
    setShowForm(false);
    setPeriodoAtual(null);
  };

  const handleSalvar = (payload) => {
    setSalvando(true);
    const isEdicao = !!(periodoAtual && periodoAtual.codigo);
    const promessa = isEdicao
      ? api.editarMatricialPeriodo(periodoAtual.codigo, payload)
      : api.incluirMatricialPeriodo(payload);

    promessa
      .then(() => {
        toast.success(isEdicao ? 'Período atualizado com sucesso.' : 'Período incluído com sucesso.');
        setShowForm(false);
        setPeriodoAtual(null);
        if (!isEdicao) setPagina(1);
        carregarPeriodos(filtroAtivo);
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao salvar período.';
        toast.error(msg);
      })
      .finally(() => setSalvando(false));
  };

  const handleSolicitarRemocao = (item) => {
    setPeriodoParaRemover(item);
    setConfirmRemover(true);
  };

  const handleConfirmarRemocao = (confirmado) => {
    if (!confirmado || !periodoParaRemover) {
      setConfirmRemover(false);
      setPeriodoParaRemover(null);
      return;
    }
    setConfirmRemover(false);
    setRemovendo(true);
    api
      .removerMatricialPeriodo(periodoParaRemover.codigo)
      .then(() => {
        toast.success('Período removido com sucesso.');
        const totalPaginas = Math.ceil((total - 1) / TAMANHO_PAGINA);
        const novaPagina = pagina > totalPaginas && totalPaginas > 0 ? totalPaginas : pagina;
        if (novaPagina !== pagina) {
          setPagina(novaPagina);
        }
        carregarPeriodos(filtroAtivo);
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao remover período.';
        toast.error(msg);
      })
      .finally(() => {
        setRemovendo(false);
        setPeriodoParaRemover(null);
      });
  };

  const handlePaginaChange = (pg) => {
    if (pg < 1 || pg > Math.ceil(total / TAMANHO_PAGINA)) return;
    setPagina(pg);
  };

  const handleBuscar = () => {
    setPagina(1);
    setFiltroAtivo(filtroDescricao.trim());
    setBuscarTick((tick) => tick + 1);
  };

  const handleFiltroKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBuscar();
    }
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Períodos — Matricial de Despesas" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-3">
          <Row className="align-items-end">
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="font-weight-bold mb-1">Pesquisar</Form.Label>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por descrição..."
                    value={filtroDescricao}
                    onChange={(e) => setFiltroDescricao(e.target.value)}
                    onKeyDown={handleFiltroKeyDown}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button variant="primary" onClick={handleBuscar} disabled={carregando}>
                <FaSearch className="mr-1" /> Buscar
              </Button>
            </Col>
            <Col className="text-right">
              <Button variant="success" onClick={handleNovo} disabled={carregando || removendo}>
                <FaPlus className="mr-1" /> Novo Período
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Períodos
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({total} {total === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
          {carregando && <small style={{ opacity: 0.8 }}>Carregando...</small>}
        </Card.Header>
        <Card.Body className="p-0">
          <GridPeriodos
            periodos={periodos}
            total={total}
            pagina={pagina}
            tamanhoPagina={TAMANHO_PAGINA}
            onPaginaChange={handlePaginaChange}
            onEditar={handleEditar}
            onRemover={handleSolicitarRemocao}
            carregando={carregando || removendo}
          />
        </Card.Body>
      </Card>

      <FormPeriodo
        show={showForm}
        periodo={periodoAtual}
        onFechar={handleFecharForm}
        onSalvar={handleSalvar}
        salvando={salvando}
      />

      <ModalConfirmacao
        show={confirmRemover}
        onClose={handleConfirmarRemocao}
        title="Excluir Período"
        message={
          periodoParaRemover
            ? `Confirma a exclusão do período "${periodoParaRemover.descricao || periodoParaRemover.codigo}"?`
            : 'Confirma a exclusão?'
        }
        variant="danger"
      />
    </Container>
  );
};

export default MatricialPeriodos;
